import React, { useEffect, useState } from 'react';
import { LayoutGrid, Printer, Search, ArrowLeft, Film, Link2 } from 'lucide-react';
import { neonQuery } from '../services/api';
import { getCurrentUser } from '../services/auth';

// Marketing > Contact Sheets. One printable page of thumbnails for a project, so a shoot can be
// found and recognized as a whole (Rebecca's ask, Nov 4, 2026). A project is a Media Intake
// batch, a project tag, or one person's uploads on one day: see netlify/functions/lib/media-projects.js.
//
//   ?page=media-contact-sheet&batch=2 | &tag=snow-crab-comparison | &session=29:2026-11-03
//   ?page=media-contact-sheet            search: describe the project, pick it
//
// CPx Ask hands out the same links (find_media_project), so "where's the lobster shoot?" lands here.
// Print uses the browser: Print > Save as PDF gives the PDF contact sheet.

const KIND = { batch: 'Media Intake project', tag: 'Tagged project', session: 'Upload' };
const day = (d) => (d ? new Date(String(d).length === 10 ? d + 'T12:00:00' : d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '');
const thumb = (key) => (key ? `/.netlify/functions/r2-image?key=${encodeURIComponent(key)}` : '');
const secs = (s) => (s == null ? '' : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, '0')}`);
const linkFor = (p) => `/?page=media-contact-sheet&${p.kind === 'batch' ? 'batch=' + p.id : p.kind === 'tag' ? 'tag=' + encodeURIComponent(p.tag) : 'session=' + encodeURIComponent(p.session)}`;

// Only the sheet prints: the CPx sidebar and the buttons drop out.
const PRINT_CSS = `@media print {
  body * { visibility: hidden !important; }
  #contact-sheet, #contact-sheet * { visibility: visible !important; }
  #contact-sheet { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
  .cs-noprint { display: none !important; }
  .cs-cell { break-inside: avoid; }
  @page { margin: 12mm; }
}`;

const S = {
  wrap: { padding: 16, display: 'flex', flexDirection: 'column', gap: 14 },
  bar: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  h: { margin: 0, color: '#003b5c', display: 'flex', alignItems: 'center', gap: 8, fontSize: 22 },
  sub: { color: '#64748b', fontSize: 13 },
  btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', color: '#003b5c', cursor: 'pointer', fontSize: 13, textDecoration: 'none' },
  primary: { background: '#003b5c', color: '#fff', borderColor: '#003b5c' },
  input: { flex: 1, minWidth: 240, padding: '8px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 14 },
  cell: { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 11, color: '#334155' },
  imgBox: { position: 'relative', width: '100%', aspectRatio: '1 / 1', background: '#f1f5f9', borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  img: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' },
  badge: { position: 'absolute', right: 4, bottom: 4, background: 'rgba(0,0,0,.7)', color: '#fff', borderRadius: 3, padding: '1px 5px', fontSize: 10, display: 'inline-flex', alignItems: 'center', gap: 3 },
  result: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 8, textDecoration: 'none', color: 'inherit', background: '#fff' },
  pill: { fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#fff4e6', color: '#7a3500', whiteSpace: 'nowrap' },
};

function Sheet({ params }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState(false);
  useEffect(() => { neonQuery('media_project_sheet', params).then(setData).catch((e) => setErr(e.message)); }, [params]);

  if (err) return <div style={S.wrap}><p style={{ color: '#b91c1c' }}>{err}</p><a style={S.btn} href="/?page=media-contact-sheet"><ArrowLeft size={14} /> Find a project</a></div>;
  if (!data) return <div style={S.wrap}><p style={S.sub}>Loading contact sheet…</p></div>;
  const { project: p, assets, truncated } = data;
  const ids = assets.map((a) => a.id);
  const copy = async () => { try { await navigator.clipboard.writeText(window.location.origin + linkFor(p)); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (_) { /* clipboard blocked */ } };

  return (
    <div style={S.wrap} id="contact-sheet">
      <style>{PRINT_CSS}</style>
      <div style={S.bar}>
        <h2 style={S.h}><LayoutGrid size={22} color="#dc582a" /> {p.name}</h2>
        <span style={S.pill}>{KIND[p.kind]}</span>
        <span style={{ flex: 1 }} />
        <button className="cs-noprint" style={S.btn} onClick={copy}><Link2 size={14} /> {copied ? 'Copied' : 'Copy link'}</button>
        <button className="cs-noprint" style={{ ...S.btn, ...S.primary }} onClick={() => window.print()}><Printer size={14} /> Print or save as PDF</button>
        <a className="cs-noprint" style={S.btn} href="/?page=media-contact-sheet"><Search size={14} /> Find another</a>
      </div>
      <div style={S.sub}>
        {[p.made_by && `By ${p.made_by}`, p.first_date && day(p.first_date) !== day(p.date) ? `${day(p.first_date)} to ${day(p.date)}` : day(p.date),
          `${assets.length}${truncated ? '+' : ''} ${assets.length === 1 ? 'item' : 'items'}`,
          ids.length ? `Media Library #${Math.min(...ids)} to #${Math.max(...ids)}` : null,
          p.kind === 'tag' && `tag: ${p.tag}`].filter(Boolean).join(' · ')}
      </div>
      {p.brief && <div style={{ fontSize: 14, color: '#334155', maxWidth: 900 }}>{p.brief}</div>}
      <div style={S.grid}>
        {assets.map((a) => (
          <div key={a.id} className="cs-cell" style={S.cell}>
            <div style={S.imgBox}>
              {a.thumb_key ? <img alt={a.name} src={thumb(a.thumb_key)} style={S.img} loading="lazy" /> : <Film size={28} color="#94a3b8" />}
              {a.kind === 'video' && <span style={S.badge}><Film size={10} /> {secs(a.duration_s)}</span>}
            </div>
            <div style={{ fontWeight: 600, color: '#003b5c' }}>#{a.id}</div>
            <div style={{ wordBreak: 'break-all' }}>{a.name}</div>
          </div>
        ))}
      </div>
      {truncated && <p style={S.sub}>Showing the first {assets.length}. Search the Media Library for the rest.</p>}
    </div>
  );
}

function Finder() {
  const user = getCurrentUser();
  const [q, setQ] = useState('');
  const [rows, setRows] = useState(null);
  const [recent, setRecent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => { neonQuery('media_batches_list', { status: 'committed', limit: 12 }).then(setRecent).catch(() => {}); }, []);

  const run = async (e) => {
    e && e.preventDefault();
    if (!q.trim()) return;
    setBusy(true); setErr('');
    try { setRows(await neonQuery('media_project_find', { q, admin_id: user?.id })); } catch (x) { setErr(x.message); }
    setBusy(false);
  };

  const card = (p, key) => (
    <a key={key} href={linkFor(p)} style={S.result}>
      <LayoutGrid size={18} color="#dc582a" />
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: '#003b5c' }}>{p.name}</div>
        <div style={S.sub}>{[day(p.date), p.asset_count != null && `${p.asset_count} items`, p.matching_assets && `${p.matching_assets} match`, (p.top_tags || []).slice(0, 4).join(', '), p.brief].filter(Boolean).join(' · ')}</div>
      </div>
      <span style={S.pill}>{KIND[p.kind]}</span>
    </a>
  );

  return (
    <div style={S.wrap}>
      <div style={S.bar}><h2 style={S.h}><LayoutGrid size={22} color="#dc582a" /> Contact Sheets</h2></div>
      <div style={S.sub}>Describe the project the way you remember it ("the lobster shoot", "snow crab comparison") and open its contact sheet. A project is a Media Intake batch, a tag a shoot shares, or one person's uploads on one day.</div>
      <form onSubmit={run} style={S.bar}>
        <input style={S.input} value={q} onChange={(e) => setQ(e.target.value)} placeholder="What was the project?" autoFocus />
        <button type="submit" style={{ ...S.btn, ...S.primary }} disabled={busy}><Search size={14} /> {busy ? 'Looking…' : 'Find'}</button>
      </form>
      {err && <p style={{ color: '#b91c1c' }}>{err}</p>}
      {rows && (rows.length ? <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{rows.map((p, i) => card(p, i))}</div>
        : <p style={S.sub}>Nothing matched. Try one or two words that would be in the photo tags, like "lobster" or "crab cakes".</p>)}
      {!rows && recent.length > 0 && (
        <>
          <div style={{ fontWeight: 600, color: '#003b5c', marginTop: 8 }}>Recent Media Intake projects</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {recent.map((b) => card({ kind: 'batch', id: b.id, name: b.name, date: b.committed_at || b.created_at, brief: b.brief }, b.id))}
          </div>
        </>
      )}
    </div>
  );
}

export default function MediaContactSheet() {
  const sp = new URLSearchParams(window.location.search);
  const [params] = useState(() => {
    const p = {};
    for (const k of ['batch', 'tag', 'session']) if (sp.get(k)) p[k] = sp.get(k);
    return p;
  });
  return Object.keys(params).length ? <Sheet params={params} /> : <Finder />;
}
